// GizmoDragInput — Fusion-style live-индикатор величины смещения тела
// при drag за TransformGizmo.
//
// Появляется около курсора при pointerdown по стрелке, обновляется на
// каждом move со значением (мм) — дельта от стартовой позиции по оси.
// Юзер может вручную вписать точное число и Enter — мы применим его как
// финальное смещение и закроем.
//
// Связь с TransformGizmo через window-callbacks:
//   __kubikonCadGizmoDragStart({axisIdx, startValue, clientX, clientY})
//   __kubikonCadGizmoDragMove({axisIdx, currentValue, startValue, clientX, clientY})
//   __kubikonCadGizmoDragEnd({axisIdx, finalValue})

import React, { useEffect, useRef, useState } from 'react';
import useCadStore from '../store/useCadStore';
import cl from './GizmoDragInput.module.css';

const AXIS_LABELS = ['X', 'Y', 'Z'];
const AXIS_COLORS = ['#cc3344', '#0c8', '#3a78ff'];


const GizmoDragInput = () => {
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const [state, setState] = useState(null);
    // null | {axisIdx, startValue, currentValue, x, y, manual}
    const inputRef = useRef(null);

    useEffect(() => {
        window.__kubikonCadGizmoDragStart = ({ axisIdx, startValue, clientX, clientY }) => {
            setState({
                axisIdx,
                startValue,
                currentValue: startValue,
                x: clientX + 16,
                y: clientY - 8,
                manual: false,
            });
        };
        window.__kubikonCadGizmoDragMove = ({ axisIdx, currentValue, startValue, clientX, clientY }) => {
            setState((prev) => {
                if (!prev || prev.axisIdx !== axisIdx) return prev;
                if (prev.manual) return prev;  // юзер уже редактирует — не перезаписываем
                return {
                    ...prev,
                    currentValue,
                    startValue,
                    x: clientX + 16,
                    y: clientY - 8,
                };
            });
        };
        window.__kubikonCadGizmoDragEnd = ({ axisIdx }) => {
            // Закрываем только если пользователь не начал ручной ввод —
            // так юзер успеет нажать Enter после отпускания мыши.
            setState((prev) => {
                if (!prev || prev.axisIdx !== axisIdx) return prev;
                if (prev.manual) return prev;
                return null;
            });
        };
        return () => {
            delete window.__kubikonCadGizmoDragStart;
            delete window.__kubikonCadGizmoDragMove;
            delete window.__kubikonCadGizmoDragEnd;
        };
    }, []);

    if (!state) return null;
    const { axisIdx, startValue, currentValue, x, y } = state;
    const delta = currentValue - startValue;

    const onChange = (e) => {
        const v = parseFloat(e.target.value);
        setState((prev) => prev ? { ...prev, currentValue: isNaN(v) ? prev.currentValue : v, manual: true } : prev);
    };

    const applyAndClose = () => {
        // Применяем точное значение к store + закрываем
        const cur = useCadStore.getState().pendingFeature;
        if (cur && cur.kind === 'primitive') {
            const next = [...(cur.position || [0, 0, 0])];
            next[axisIdx] = Math.round(currentValue * 100) / 100;
            updatePendingFeature({ position: next });
            // Дёргаем live-обновление, чтобы тело сразу встало в новую позицию
            const live = window.__kubikonCadPrimitiveLive;
            if (typeof live === 'function') {
                try { live({ position: next }); } catch (_) {}
            }
        } else if (cur && cur.kind === 'moveCopy') {
            // Inline-инпут показывает АБСОЛЮТНОЕ значение оси, но startValue
            // в moveCopy — это центр body + текущее dx/dy/dz. Дельта от
            // startValue даёт изменение dx/dy/dz относительно прежнего.
            const delta = Math.round((currentValue - startValue) * 100) / 100;
            const key = ['dx', 'dy', 'dz'][axisIdx];
            const prev = Math.round((cur[key] || 0) * 100) / 100;
            updatePendingFeature({ [key]: prev + delta });
        }
        setState(null);
    };

    const onKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            applyAndClose();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setState(null);
        }
    };

    return (
        <div
            className={cl.box}
            style={{ left: x, top: y, borderColor: AXIS_COLORS[axisIdx] }}
            onPointerDown={(e) => {
                // Юзер кликнул в инпут — фиксируем что он редактирует вручную
                e.stopPropagation();
                setState((prev) => prev ? { ...prev, manual: true } : prev);
                setTimeout(() => inputRef.current?.focus(), 0);
            }}
        >
            <span
                className={cl.axisBadge}
                style={{ background: AXIS_COLORS[axisIdx] }}
            >
                {AXIS_LABELS[axisIdx]}
            </span>
            <input
                ref={inputRef}
                type="number"
                step="any"
                className={cl.input}
                value={Math.round(delta * 100) / 100}
                onChange={(e) => {
                    // delta-инпут: интерпретируем как смещение от startValue
                    const dv = parseFloat(e.target.value);
                    if (!isNaN(dv)) {
                        setState((prev) => prev
                            ? { ...prev, currentValue: prev.startValue + dv, manual: true }
                            : prev);
                    } else {
                        onChange(e);
                    }
                }}
                onKeyDown={onKey}
            />
            <span className={cl.unit}>мм</span>
            <button className={cl.okBtn} onClick={applyAndClose} title="Применить (Enter)">✓</button>
        </div>
    );
};

export default GizmoDragInput;
